import type {
	VisionDetectionsFile,
	VisionGeminiBoxesFile,
	VisionPoint,
} from "@sitescene/types";

import { type RefObject, useEffect, useRef } from "react";

import { classColor } from "./format";
import { type Rect, videoRect } from "./video-rect";

/** A line or area being drawn, or shown from the plan. */
export interface OverlayShape {
	area?: VisionPoint[];
	line?: { a: VisionPoint; b: VisionPoint };
	/** Points placed so far while drawing. */
	pending?: VisionPoint[];
	/** The plan's focus point, ringed. */
	point?: VisionPoint;
}

interface BoxOverlayProps {
	/** Track id → its class, from the run's objects. */
	classOf: Map<number, string>;
	/** Track ids the count rule counted, drawn heavier. */
	counted: Set<number>;
	detections?: VisionDetectionsFile;
	geminiBoxes?: VisionGeminiBoxesFile;
	hiddenClasses: Set<string>;
	/** Track id → label, e.g. "#12 person · red jacket". */
	labels: Map<number, string>;
	/** Clicks place points while set; otherwise clicks reach the video. */
	onPoint?: (point: VisionPoint) => void;
	selectedId?: number;
	shape?: OverlayShape;
	videoRef: RefObject<HTMLVideoElement | null>;
}

const GEMINI_COLOR = "#f59e0b";
const SHAPE_COLOR = "#facc15";

/**
 * Draws a run's boxes over the playing video, in step with it: RF-DETR's
 * boxes interpolated between the sampled frames, Gemini's target boxes
 * (dashed) on the frames it looked at, and the plan's line or area. The
 * canvas lets clicks through to the video's controls unless a shape is being
 * drawn.
 */
export const BoxOverlay = ({
	classOf,
	counted,
	detections,
	geminiBoxes,
	hiddenClasses,
	labels,
	onPoint,
	selectedId,
	shape,
	videoRef,
}: BoxOverlayProps) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	// The latest props, read by the draw loop without restarting it
	const props = useRef({
		classOf,
		counted,
		detections,
		geminiBoxes,
		hiddenClasses,
		labels,
		selectedId,
		shape,
	});
	useEffect(() => {
		props.current = {
			classOf,
			counted,
			detections,
			geminiBoxes,
			hiddenClasses,
			labels,
			selectedId,
			shape,
		};
	});

	useEffect(() => {
		let frame = 0;
		const draw = () => {
			frame = requestAnimationFrame(draw);
			const canvas = canvasRef.current;
			const video = videoRef.current;
			if (!canvas || !video) return;
			const context = canvas.getContext("2d");
			if (!context) return;

			const ratio = window.devicePixelRatio || 1;
			const width = canvas.clientWidth;
			const height = canvas.clientHeight;
			if (
				canvas.width !== Math.round(width * ratio) ||
				canvas.height !== Math.round(height * ratio)
			) {
				canvas.width = Math.round(width * ratio);
				canvas.height = Math.round(height * ratio);
			}
			context.setTransform(ratio, 0, 0, ratio, 0, 0);
			context.clearRect(0, 0, width, height);

			const view = videoRect(video, width, height);
			if (!view) return;
			const current = props.current;
			drawShape(context, view, current.shape);
			if (current.detections) {
				drawDetections(context, view, video.currentTime, current);
			}
			if (current.geminiBoxes) {
				drawGemini(context, view, video.currentTime, current.geminiBoxes);
			}
		};
		frame = requestAnimationFrame(draw);
		return () => cancelAnimationFrame(frame);
	}, [videoRef]);

	return (
		<canvas
			className="absolute inset-0 h-full w-full"
			onClick={(event) => {
				const canvas = canvasRef.current;
				const video = videoRef.current;
				if (!onPoint || !canvas || !video) return;
				const bounds = canvas.getBoundingClientRect();
				const view = videoRect(video, bounds.width, bounds.height);
				if (!view) return;
				const x = (event.clientX - bounds.left - view.x) / view.width;
				const y = (event.clientY - bounds.top - view.y) / view.height;
				if (x < 0 || x > 1 || y < 0 || y > 1) return;
				onPoint([round(x), round(y)]);
			}}
			ref={canvasRef}
			style={{
				cursor: onPoint ? "crosshair" : undefined,
				pointerEvents: onPoint ? "auto" : "none",
			}}
		/>
	);
};

const toPixels = (view: Rect, [x, y]: VisionPoint): [number, number] => [
	view.x + x * view.width,
	view.y + y * view.height,
];

function drawDetections(
	context: CanvasRenderingContext2D,
	view: Rect,
	now: number,
	current: Pick<
		BoxOverlayProps,
		| "classOf"
		| "counted"
		| "detections"
		| "hiddenClasses"
		| "labels"
		| "selectedId"
	>,
) {
	const file = current.detections;
	if (!file?.frames.length) return;
	const index = lastFrameAtOrBefore(file.frames, now);
	if (index < 0) return;
	const before = file.frames[index];
	const after = file.frames[index + 1];
	if (!before) return;
	const step = 1 / file.fps;
	// Past the last frame by more than a step, the boxes are gone
	if (!after && now - before.t > step) return;

	const nextById = new Map((after?.boxes ?? []).map((box) => [box[0], box]));
	const blend = after
		? Math.min(1, Math.max(0, (now - before.t) / (after.t - before.t || step)))
		: 0;
	context.font = "12px sans-serif";
	context.setLineDash([]);

	for (const box of before.boxes) {
		const [id, x1, y1, x2, y2] = box;
		const name = current.classOf.get(id) ?? "object";
		if (current.hiddenClasses.has(name)) continue;
		const next = nextById.get(id);
		const at = (from: number, to: number | undefined) =>
			to === undefined ? from : from + (to - from) * blend;
		const [left, top] = toPixels(view, [at(x1, next?.[1]), at(y1, next?.[2])]);
		const [right, bottom] = toPixels(view, [
			at(x2, next?.[3]),
			at(y2, next?.[4]),
		]);

		const color = classColor(name);
		const selected = current.selectedId === id;
		context.strokeStyle = color;
		context.lineWidth = selected ? 4 : current.counted.has(id) ? 3 : 1.5;
		context.strokeRect(left, top, right - left, bottom - top);

		const label = current.labels.get(id) ?? `#${id} ${name}`;
		const textWidth = context.measureText(label).width + 8;
		context.fillStyle = color;
		context.fillRect(left, Math.max(view.y, top - 16), textWidth, 16);
		context.fillStyle = "#0b0b0b";
		context.fillText(label, left + 4, Math.max(view.y, top - 16) + 12);
	}
}

function drawGemini(
	context: CanvasRenderingContext2D,
	view: Rect,
	now: number,
	file: VisionGeminiBoxesFile,
) {
	if (!file.frames.length) return;
	// The nearest frame Gemini looked at, if it's within a second
	let nearest = file.frames[0];
	for (const frame of file.frames) {
		if (nearest && Math.abs(frame.t - now) < Math.abs(nearest.t - now))
			nearest = frame;
	}
	if (!nearest || Math.abs(nearest.t - now) > 1) return;

	context.strokeStyle = GEMINI_COLOR;
	context.lineWidth = 2.5;
	context.setLineDash([6, 4]);
	context.font = "12px sans-serif";
	for (const { box, label } of nearest.boxes) {
		const [left, top] = toPixels(view, [box[0], box[1]]);
		const [right, bottom] = toPixels(view, [box[2], box[3]]);
		context.strokeRect(left, top, right - left, bottom - top);
		const text = `Gemini: ${label}`;
		const textWidth = context.measureText(text).width + 8;
		context.fillStyle = GEMINI_COLOR;
		context.fillRect(left, Math.max(view.y, top - 16), textWidth, 16);
		context.fillStyle = "#0b0b0b";
		context.fillText(text, left + 4, Math.max(view.y, top - 16) + 12);
	}
	context.setLineDash([]);
}

function drawShape(
	context: CanvasRenderingContext2D,
	view: Rect,
	shape?: OverlayShape,
) {
	if (!shape) return;
	context.strokeStyle = SHAPE_COLOR;
	context.fillStyle = SHAPE_COLOR;
	context.lineWidth = 3;
	context.setLineDash([]);

	if (shape.point) {
		const [px, py] = toPixels(view, shape.point);
		context.beginPath();
		context.arc(px, py, 10, 0, Math.PI * 2);
		context.stroke();
		context.beginPath();
		context.arc(px, py, 2.5, 0, Math.PI * 2);
		context.fill();
	}

	const points = shape.pending?.length
		? shape.pending
		: shape.line
			? [shape.line.a, shape.line.b]
			: (shape.area ?? []);
	if (!points.length) return;

	context.beginPath();
	points.forEach((point, index) => {
		const [px, py] = toPixels(view, point);
		if (index === 0) context.moveTo(px, py);
		else context.lineTo(px, py);
	});
	if (shape.area && !shape.pending?.length) {
		context.closePath();
		context.globalAlpha = 0.15;
		context.fill();
		context.globalAlpha = 1;
	}
	context.stroke();
	for (const point of points) {
		const [px, py] = toPixels(view, point);
		context.beginPath();
		context.arc(px, py, 4, 0, Math.PI * 2);
		context.fill();
	}

	// An arrow across the line toward its right side, where "in" goes
	if (shape.line && !shape.pending?.length) {
		const [ax, ay] = toPixels(view, shape.line.a);
		const [bx, by] = toPixels(view, shape.line.b);
		const [mx, my] = [(ax + bx) / 2, (ay + by) / 2];
		const length = Math.hypot(bx - ax, by - ay) || 1;
		// On screen (y down) the right of a→b is (−dy, dx)
		const [nx, ny] = [-(by - ay) / length, (bx - ax) / length];
		context.beginPath();
		context.moveTo(mx, my);
		context.lineTo(mx + nx * 24, my + ny * 24);
		context.stroke();
		context.font = "12px sans-serif";
		context.fillText("in", mx + nx * 30, my + ny * 30);
	}
}

/** Binary search for the last frame at or before `t`. */
const lastFrameAtOrBefore = (
	frames: VisionDetectionsFile["frames"],
	t: number,
): number => {
	let low = 0;
	let high = frames.length - 1;
	let found = -1;
	while (low <= high) {
		const middle = (low + high) >> 1;
		if ((frames[middle]?.t ?? 0) <= t) {
			found = middle;
			low = middle + 1;
		} else {
			high = middle - 1;
		}
	}
	return found;
};

const round = (value: number) => Math.round(value * 1000) / 1000;
